import { Injectable } from '@angular/core';
import { EventName, Events } from '../utils/events';
import { API } from './api';
import { JobProvider } from './job';
import { Palette } from '../components/export/palette-editor';
import { ToastController } from 'ionic-angular';
import { TranslateService } from '@ngx-translate/core';

export enum ExportType {
    Segmentation = 'SEGMENTATION',
    Detection = 'BOUNDING_BOX',
    Json = 'RAW_JSON'
}

export enum ExportFramework {
    TensorFlow = 'TENSORFLOW',
    Caffe = 'CAFFE',
    Json = 'RAW_JSON'
}

export interface ExportClass {
    name: string;
    color: string;
    id: string;
    datasetIdentifier: string;
    alias?: string;
}

export class Export {
    id: string;
    framework: ExportFramework;
    type: ExportType;
    classes: ExportClass[] = [];
    datasets: any = [];
    projects: any = [];
    mask: string;
    includeImages: boolean;
    resizeImages: boolean;
    imageWidth: number;
    imageHeight: number;
    trainingPercent: number;
    validationPercent: number;
    testPercent: number;
    trainingImageCount: number;
    validationImageCount: number;
    testImageCount: number;
    sendEmail: boolean;
    totalImages: number;
    palette: Palette | undefined;
    dateStarted: Date;
    dateFinished: Date | null;
    dateFailed: Date | null;
    signedUrl: string;
    normalizeBoundingBoxes?: boolean;
    useExistingSets?: boolean;
}

@Injectable()
export class ExportProvider {
    public exportModelOpen = false;

    constructor(
        private api: API,
        private jobProvider: JobProvider,
        private events: Events,
        private toastCtrl: ToastController,
        private translate: TranslateService
    ) {}

    /**
     * Creates a new export
     */
    public async exportData(exp: Export): Promise<string> {
        try {
            const newExport = await this.api.post('exports/', {
                exportType: exp.type,
                framework: exp.framework,
                options: {
                    mask: exp.mask,
                    include_images: exp.includeImages,
                    resize_images: exp.resizeImages,
                    image_width: exp.imageWidth,
                    image_height: exp.imageHeight,
                    train_percent: exp.trainingPercent,
                    validation_percent: exp.validationPercent,
                    test_percent: exp.testPercent,
                    send_email: exp.sendEmail,
                    normalize_bounding_boxes: exp.normalizeBoundingBoxes,
                    palette: exp.palette,
                    use_existing_sets: exp.useExistingSets
                },
                classes: exp.classes.map(cls => {
                    return {
                        classIdentifier: cls.id,
                        name: cls.name,
                        color: cls.color,
                        alias: cls.alias
                    };
                }),
                datasets: exp.datasets.map(dataset => {
                    return { datasetIdentifier: dataset.id };
                }),

                projects: exp.projects.map(project => {
                    return { projectIdentifier: project.id };
                })
            });
            this.jobProvider.pollJob(newExport.message.exportIdentifier, async job => {
                this.events.publish(EventName.ExportDone);
                if (!this.exportModelOpen) {
                    this.toastCtrl
                        .create({
                            message:
                                job.state == 'Done'
                                    ? this.translate.instant('Your export has completed')
                                    : this.translate.instant('Your export has failed'),
                            duration: 2000
                        })
                        .present();
                }
            });
            return newExport.message.exportIdentifier;
        } catch (e) {
            throw e;
        }
    }

    /**
     * Get all exports for user
     */
    public async getUserExports(limit?: number, offset?: number): Promise<any> {
        try {
            const params: string[] = [];

            if (limit) {
                params.push(`limit=${limit}`);
            }
            if (offset) {
                params.push(`offset=${offset}`);
            }

            // tslint:disable-next-line: prefer-template
            const queryString = 'exports' + (params ? '?' + params.join('&') : '');

            const myExports = await this.api.get(queryString);
            return {
                exports: myExports.message.exports.map(exp => {
                    return this.getExportObject(exp);
                }),
                total: myExports.message.count
            };
        } catch (e) {
            throw e;
        }
    }

    /**
     * Get details of a specific export
     */
    public async getExport(exportIdentifier): Promise<Export> {
        try {
            const myExport = await this.api.get(`exports/${exportIdentifier}`);
            return this.getExportObject(myExport.message);
        } catch (e) {
            throw e;
        }
    }

    /**
     * Deletes an export
     */
    public async deleteExport(exportIdentifier): Promise<any> {
        try {
            return await this.api.delete(`exports/${exportIdentifier}`);
        } catch (e) {
            throw e;
        }
    }

    /**
     * Converts api response for export into export object
     */
    private getExportObject(exp: any): Export {
        const myExport = new Export();
        myExport.id = exp.exportIdentifier;
        myExport.framework = exp.framework;
        myExport.type = exp.exportType;
        myExport.mask = exp.options.mask;
        myExport.includeImages = exp.options.includeImages;
        myExport.resizeImages = exp.options.resizeImages;
        myExport.imageWidth = exp.options.imageWidth;
        myExport.imageHeight = exp.options.imageHeight;
        myExport.trainingPercent = exp.options.trainingPercent;
        myExport.validationPercent = exp.options.validationPercent;
        myExport.testPercent = exp.options.testPercent;
        myExport.trainingImageCount = exp.options.trainingImageCount;
        myExport.validationImageCount = exp.options.validationImageCount;
        myExport.testImageCount = exp.options.testImageCount;
        myExport.totalImages = exp.totalImages;
        myExport.dateStarted = exp.dateStarted ? new Date(exp.dateStarted) : new Date();
        myExport.dateFinished = exp.dateFinished ? new Date(exp.dateFinished) : null;
        myExport.dateFailed = exp.dateFailed ? new Date(exp.dateFailed) : null;
        myExport.signedUrl = exp.signedUrl;
        myExport.classes = exp.classes.map(cls => {
            return {
                id: cls.classIdentifier,
                name: cls.name,
                color: cls.color
            };
        });

        if (exp.datasets) {
            myExport.datasets = exp.datasets;
        }

        if (exp.projects) {
            myExport.projects = exp.projects;
        }

        if (exp.type == ExportType.Detection) {
            myExport.normalizeBoundingBoxes = exp.options.normalizeBoundingBoxes;
        }

        return myExport;
    }
}



// WEBPACK FOOTER //
// ./src/providers/export.ts